import { Button, Card, CardBody, Table } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faUsersSlash,
  faPlus,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { RoutePaths } from "../RoutePaths";
import { renderLoadingUntilDefined } from "../common/LoadingUntilDefined";
import {
  leaveCrewMutation,
  addUserToCrewMutation,
  useCrewsByOrganizationQuery,
  useCrewsQuery,
} from "./useCrewsQuery";
import { CrewInformationUb, OrganizationCrewUb } from "../gql/graphql";
import { UiConfig } from "../Config";
import { ButtonWithDisabledReason } from "../common/components/buttonwithdisabledreason/ButtonWithDisabledReason";
import { CopyToClipboardButton } from "../common/CopyToClipboardButton";
import { CyCrewSettings } from "./CyCrewSettings";
import { useTranslation } from "react-i18next";
import { useState, useMemo, useEffect } from "react";
import { CrewManagementDialog } from "./CrewManagementDialog";
import { DeleteCrewDialog } from "./DeleteCrewDialog";
import { ManagementPanel } from "../common/components/management/ManagementPanel";
import { useClient } from "urql";
import "./CrewSettings.css";

export function CrewSettings() {
  const { crews, reexecuteQuery: reexecuteCrewsQuery } = useCrewsQuery();
  const { t } = useTranslation();
  const client = useClient();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedBucket, setSelectedBucket] = useState<string>("");
  const [editingCrew, setEditingCrew] = useState<CrewInformationUb | undefined>(
    undefined,
  );
  const [editingCrewIsPrivate, setEditingCrewIsPrivate] = useState(false);
  const [editingCrewIsMemberOfCrew, setEditingCrewIsMemberOfCrew] =
    useState(true);
  const [crewToDelete, setCrewToDelete] = useState<
    OrganizationCrewUb | undefined
  >(undefined);
  const [leavingCrewIdentifier, setLeavingCrewIdentifier] = useState<
    string | undefined
  >(undefined);
  const [joiningCrewIdentifier, setJoiningCrewIdentifier] = useState<
    string | undefined
  >(undefined);

  const availableBuckets = useMemo(() => {
    if (!crews) return [];
    const buckets = new Set(crews.map((c) => c.bucket));
    return Array.from(buckets).sort();
  }, [crews]);

  const effectiveSelectedBucket = selectedBucket || availableBuckets[0] || "";
  const {
    crewsByOrganization,
    reexecuteQuery: reexecuteCrewsByOrganizationQuery,
  } = useCrewsByOrganizationQuery(effectiveSelectedBucket);
  const crewIdentifiersOfCurrentUser = useMemo(() => {
    if (!crews) {
      return new Set<string>();
    }
    return new Set(crews.map((crew) => crew.identifier));
  }, [crews]);

  const crewsInSelectedBucket = useMemo(() => {
    if (!crewsByOrganization) return [];
    return [...crewsByOrganization].sort((a, b) =>
      a.title.localeCompare(b.title),
    );
  }, [crewsByOrganization]);

  // Set first available bucket on initial load
  useEffect(() => {
    if (availableBuckets.length > 0 && !selectedBucket) {
      setSelectedBucket(availableBuckets[0]);
    }
  }, [availableBuckets, selectedBucket]);

  useEffect(() => {
    if (!selectedBucket) {
      return;
    }
    if (availableBuckets.length === 0) {
      setSelectedBucket("");
      return;
    }
    if (!availableBuckets.includes(selectedBucket)) {
      setSelectedBucket(availableBuckets[0]);
    }
  }, [availableBuckets, selectedBucket]);

  function generateJoiningLink(joiningIdentifier: string) {
    return `${UiConfig.baseUrl}/${RoutePaths.JOIN_CREW}/${joiningIdentifier}`;
  }

  function toCrewInformation(crew: OrganizationCrewUb): CrewInformationUb {
    return {
      __typename: "CrewInformationUB",
      identifier: crew.identifier,
      joiningIdentifier: crew.joiningIdentifier,
      title: crew.title,
      bucket: effectiveSelectedBucket,
    };
  }

  function isCrewPublic(crew: OrganizationCrewUb): boolean {
    return !crew.isPrivateCrew;
  }

  function isCurrentUserMemberOfCrew(crew: OrganizationCrewUb): boolean {
    return crewIdentifiersOfCurrentUser.has(crew.identifier);
  }

  function isLastMemberOfPrivateCrew(crew: OrganizationCrewUb): boolean {
    return (
      !isCrewPublic(crew) &&
      crew.members.length === 1 &&
      isCurrentUserMemberOfCrew(crew)
    );
  }

  async function handleLeaveCrew(crewTitle: string, crewIdentifier: string) {
    const confirmed = window.confirm(
      t("crew.manage.leaveConfirm", { crewTitle }),
    );
    if (!confirmed || leavingCrewIdentifier) {
      return;
    }

    setLeavingCrewIdentifier(crewIdentifier);
    const result = await client
      .mutation(leaveCrewMutation, { identifier: crewIdentifier })
      .toPromise();
    setLeavingCrewIdentifier(undefined);

    if (result.error || !result.data) {
      console.error("Failed to leave crew", result.error);
      window.alert(t("crew.manage.leaveError"));
      return;
    }

    reexecuteCrewsByOrganizationQuery({ requestPolicy: "network-only" });
    reexecuteCrewsQuery({ requestPolicy: "network-only" });
  }

  async function handleJoinCrew(joiningIdentifier: string) {
    if (joiningCrewIdentifier) {
      return;
    }

    setJoiningCrewIdentifier(joiningIdentifier);
    const result = await client
      .mutation(addUserToCrewMutation, {
        joiningIdentifier: joiningIdentifier,
      })
      .toPromise();
    setJoiningCrewIdentifier(undefined);

    if (result.error || !result.data) {
      console.error("Failed to join crew", result.error);
      window.alert(t("crew.manage.joinError"));
      return;
    }

    reexecuteCrewsByOrganizationQuery({ requestPolicy: "network-only" });
    reexecuteCrewsQuery({ requestPolicy: "network-only" });
  }

  const openCreateDialog = (bucket: string) => {
    setSelectedBucket(bucket);
    setEditingCrew(undefined);
    setEditingCrewIsPrivate(false);
    setEditingCrewIsMemberOfCrew(true);
    setIsDialogOpen(true);
  };

  const openEditDialog = (crew: OrganizationCrewUb) => {
    setSelectedBucket(effectiveSelectedBucket);
    setEditingCrew(toCrewInformation(crew));
    setEditingCrewIsPrivate(crew.isPrivateCrew);
    setEditingCrewIsMemberOfCrew(isCurrentUserMemberOfCrew(crew));
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (crew: OrganizationCrewUb) => {
    setCrewToDelete(crew);
    setIsDeleteDialogOpen(true);
  };

  return (
    <>
      <div data-cy={CyCrewSettings.CrewList}>
        {renderLoadingUntilDefined(crews, (crewInformation) => {
          if (crewInformation.length === 0) {
            return (
              <p>
                <span style={{ width: "30px", display: "inline-block" }}>
                  <FontAwesomeIcon icon={faUsersSlash} />
                </span>
                {t("crew.manage.noCrewFound")}
              </p>
            );
          } else {
            return (
              <>
                <div className="crew-settings-org-label">
                  {t("crew.manage.organization")}
                </div>
                <div className="crew-settings-org-switcher mb-4">
                  {availableBuckets.map((bucket) => (
                    <Button
                      key={bucket}
                      color={
                        effectiveSelectedBucket === bucket ? "primary" : "light"
                      }
                      size="sm"
                      className="crew-settings-org-button"
                      data-cy={CyCrewSettings.OrganizationButton}
                      data-cy-key={bucket}
                      onClick={() => setSelectedBucket(bucket)}
                    >
                      {bucket}
                    </Button>
                  ))}
                </div>

                <ManagementPanel
                  title={effectiveSelectedBucket}
                  actions={
                    <Button
                      color="primary"
                      data-cy={CyCrewSettings.CreateCrewButton}
                      onClick={() => openCreateDialog(effectiveSelectedBucket)}
                      disabled={!effectiveSelectedBucket}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      {t("crew.manage.createNewCrew")}
                    </Button>
                  }
                >
                  {crewsByOrganization === undefined ? (
                    <p className="text-muted management-empty">
                      {t("crew.manage.loadingCrews")}
                    </p>
                  ) : crewsInSelectedBucket.length === 0 ? (
                    <p className="text-muted management-empty">
                      {t("crew.manage.noCrewsInOrganization")}
                    </p>
                  ) : (
                    <>
                      <div className="d-none d-lg-block">
                        <Table hover className="management-table mb-0">
                          <thead>
                            <tr>
                              <th>{t("crew.manage.crewName")}</th>
                              <th>{t("crew.manage.visibility")}</th>
                              <th>{t("crew.manage.membership")}</th>
                              <th>{t("crew.manage.playlists")}</th>
                              <th>{t("crew.manage.members")}</th>
                              <th>{t("crew.manage.joiningLink")}</th>
                              <th className="text-end">
                                {t("crew.manage.action")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {crewsInSelectedBucket.map((crew) => {
                              const joinedByCurrentUser =
                                isCurrentUserMemberOfCrew(crew);
                              const publicCrew = isCrewPublic(crew);
                              const lastMemberOfPrivateCrew =
                                isLastMemberOfPrivateCrew(crew);
                              return (
                                <tr key={crew.identifier}>
                                  <td>
                                    <span className="crew-settings-crew-icon">
                                      <FontAwesomeIcon icon={faUsers} />
                                    </span>
                                    <span className="fw-semibold">
                                      {crew.title}
                                    </span>
                                  </td>
                                  <td>
                                    {publicCrew
                                      ? t("crew.manage.public")
                                      : t("crew.manage.private")}
                                  </td>
                                  <td>
                                    {joinedByCurrentUser
                                      ? t("crew.manage.joined")
                                      : t("crew.manage.notJoined")}
                                  </td>
                                  <td>{crew.playlistCount}</td>
                                  <td>{crew.members.length}</td>
                                  <td>
                                    <CopyToClipboardButton
                                      buttonValue={t(
                                        "crew.manage.copyInvitation",
                                      )}
                                      content={generateJoiningLink(
                                        crew.joiningIdentifier,
                                      )}
                                      buttonSize="sm"
                                    />
                                  </td>
                                  <td className="text-end">
                                    <div className="management-action-group">
                                      <Button
                                        color="primary"
                                        size="sm"
                                        data-cy={CyCrewSettings.EditCrewButton}
                                        data-cy-key={`desktop-${crew.identifier}`}
                                        onClick={() => openEditDialog(crew)}
                                      >
                                        <FontAwesomeIcon icon={faEdit} />
                                      </Button>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        data-cy={
                                          CyCrewSettings.DeleteCrewButton
                                        }
                                        data-cy-key={`desktop-${crew.identifier}`}
                                        onClick={() => handleDeleteClick(crew)}
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </Button>
                                      {joinedByCurrentUser ? (
                                        <ButtonWithDisabledReason
                                          color="warning"
                                          size="sm"
                                          className="crew-settings-membership-button"
                                          data-cy={
                                            CyCrewSettings.LeaveCrewButton
                                          }
                                          data-cy-key={`desktop-${crew.identifier}`}
                                          disabled={
                                            leavingCrewIdentifier ===
                                            crew.identifier
                                          }
                                          disabledReason={
                                            lastMemberOfPrivateCrew
                                              ? t(
                                                  "crew.manage.leaveBlockedLastMember",
                                                )
                                              : undefined
                                          }
                                          onClick={() =>
                                            handleLeaveCrew(
                                              crew.title,
                                              crew.identifier,
                                            )
                                          }
                                        >
                                          {leavingCrewIdentifier ===
                                          crew.identifier
                                            ? t("crew.manage.leaving")
                                            : t("crew.manage.leave")}
                                        </ButtonWithDisabledReason>
                                      ) : (
                                        <Button
                                          color="success"
                                          size="sm"
                                          className="crew-settings-membership-button"
                                          data-cy={
                                            CyCrewSettings.JoinCrewButton
                                          }
                                          data-cy-key={`desktop-${crew.identifier}`}
                                          disabled={
                                            joiningCrewIdentifier ===
                                              crew.joiningIdentifier ||
                                            !isCrewPublic(crew)
                                          }
                                          onClick={() =>
                                            handleJoinCrew(
                                              crew.joiningIdentifier,
                                            )
                                          }
                                        >
                                          {joiningCrewIdentifier ===
                                          crew.joiningIdentifier
                                            ? t("crew.manage.joining")
                                            : t("crew.manage.join")}
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      <div className="d-lg-none crew-settings-mobile-list">
                        {crewsInSelectedBucket.map((crew) => {
                          const joinedByCurrentUser =
                            isCurrentUserMemberOfCrew(crew);
                          const publicCrew = isCrewPublic(crew);
                          const lastMemberOfPrivateCrew =
                            isLastMemberOfPrivateCrew(crew);
                          return (
                            <Card
                              key={crew.identifier}
                              className="crew-settings-mobile-card"
                            >
                              <CardBody>
                                <h5 className="crew-settings-mobile-title mb-3">
                                  <FontAwesomeIcon
                                    icon={faUsers}
                                    className="me-2"
                                  />
                                  {crew.title}
                                </h5>

                                <div className="mb-2">
                                  <small className="text-muted d-block">
                                    {t("crew.manage.visibility")}:{" "}
                                    {publicCrew
                                      ? t("crew.manage.public")
                                      : t("crew.manage.private")}
                                  </small>
                                  <small className="text-muted d-block">
                                    {t("crew.manage.membership")}:{" "}
                                    {joinedByCurrentUser
                                      ? t("crew.manage.joined")
                                      : t("crew.manage.notJoined")}
                                  </small>
                                </div>

                                <div className="crew-settings-mobile-stats">
                                  <div>
                                    <div className="crew-settings-stat-label">
                                      {t("crew.manage.playlists")}
                                    </div>
                                    <div className="crew-settings-stat-value">
                                      {crew.playlistCount}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="crew-settings-stat-label">
                                      {t("crew.manage.members")}
                                    </div>
                                    <div className="crew-settings-stat-value">
                                      {crew.members.length}
                                    </div>
                                  </div>
                                </div>

                                <div className="crew-settings-mobile-copy mb-3">
                                  <CopyToClipboardButton
                                    buttonValue={t(
                                      "crew.manage.copyInvitation",
                                    )}
                                    content={generateJoiningLink(
                                      crew.joiningIdentifier,
                                    )}
                                  />
                                </div>

                                <div className="crew-settings-mobile-actions">
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      data-cy={CyCrewSettings.EditCrewButton}
                                      data-cy-key={`mobile-${crew.identifier}`}
                                      onClick={() => openEditDialog(crew)}
                                    >
                                      <FontAwesomeIcon icon={faEdit} />
                                    </Button>
                                    <Button
                                      color="danger"
                                      data-cy={CyCrewSettings.DeleteCrewButton}
                                      data-cy-key={`mobile-${crew.identifier}`}
                                      onClick={() => handleDeleteClick(crew)}
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  </div>
                                  {joinedByCurrentUser ? (
                                    <ButtonWithDisabledReason
                                      color="warning"
                                      className="crew-settings-membership-button"
                                      data-cy={CyCrewSettings.LeaveCrewButton}
                                      data-cy-key={`mobile-${crew.identifier}`}
                                      disabled={
                                        leavingCrewIdentifier ===
                                        crew.identifier
                                      }
                                      disabledReason={
                                        lastMemberOfPrivateCrew
                                          ? t(
                                              "crew.manage.leaveBlockedLastMember",
                                            )
                                          : undefined
                                      }
                                      onClick={() =>
                                        handleLeaveCrew(
                                          crew.title,
                                          crew.identifier,
                                        )
                                      }
                                    >
                                      {leavingCrewIdentifier === crew.identifier
                                        ? t("crew.manage.leaving")
                                        : t("crew.manage.leave")}
                                    </ButtonWithDisabledReason>
                                  ) : (
                                    <Button
                                      color="success"
                                      className="crew-settings-membership-button"
                                      data-cy={CyCrewSettings.JoinCrewButton}
                                      data-cy-key={`mobile-${crew.identifier}`}
                                      disabled={
                                        joiningCrewIdentifier ===
                                          crew.joiningIdentifier ||
                                        !isCrewPublic(crew)
                                      }
                                      onClick={() =>
                                        handleJoinCrew(crew.joiningIdentifier)
                                      }
                                    >
                                      {joiningCrewIdentifier ===
                                      crew.joiningIdentifier
                                        ? t("crew.manage.joining")
                                        : t("crew.manage.join")}
                                    </Button>
                                  )}
                                </div>
                              </CardBody>
                            </Card>
                          );
                        })}
                      </div>
                    </>
                  )}
                </ManagementPanel>
              </>
            );
          }
        })}

        <CrewManagementDialog
          isOpen={isDialogOpen}
          toggle={() => setIsDialogOpen(!isDialogOpen)}
          bucket={selectedBucket}
          crew={editingCrew}
          isPrivateCrew={editingCrewIsPrivate}
          isMemberOfCrew={editingCrewIsMemberOfCrew}
          onSaved={() => {
            reexecuteCrewsByOrganizationQuery({
              requestPolicy: "network-only",
            });
            reexecuteCrewsQuery({ requestPolicy: "network-only" });
          }}
        />

        <DeleteCrewDialog
          isOpen={isDeleteDialogOpen}
          toggle={() => setIsDeleteDialogOpen(!isDeleteDialogOpen)}
          crew={crewToDelete}
          onDeleted={() => {
            setIsDeleteDialogOpen(false);
            reexecuteCrewsByOrganizationQuery({
              requestPolicy: "network-only",
            });
            reexecuteCrewsQuery({ requestPolicy: "network-only" });
          }}
        />
      </div>
    </>
  );
}
